import { defineStore } from 'pinia'
import api from '@/api'
import {
	ANALYTIC_AGE_TO_AGE_RANGE_MAP,
	VALUES_MAP_FOR_LEGACY_ANALYTICS_BY_PROPERTY,
	REQUIRED_ANALYTIC_PROPERTIES,
	REQUIRED_ANALYTIC_PROPERTIES_VALUES_MAP,
} from '@/helpers/constants'
import { round } from '@/helpers/utils'
import { capitalize } from 'lodash-es'
import { convertHeight, convertWeights } from '@/helpers/userData'
import Sentry from '@/modules/Sentry'
import { useMainStore } from './main'
import { useLocationStore } from './location'
import { useI18nStore } from './i18n'
import { useSubscriptionStore } from './subscription'
import { weeklyStorage, sessionOrPreviewStorage, sharedKeyPersist } from './persistence'

// Vue 2 backward-compat: vuex-persistedstate used to restore ALL keys from the
// sessionStorage blob regardless of configured paths, so tests that set
// sessionStorage['vuex'].survey accidentally worked even though 'survey' wasn't
// in the sessionStorage plugin's paths. Pinia doesn't do this — we replicate
// the behaviour by patching from sessionStorage in afterRestore.
// For real users, sessionStorage['vuex'] never contains a 'survey' key, so this is a no-op.
function _patchFromSessionStorage(context) {
	try {
		const raw = sessionOrPreviewStorage.getItem('vuex')
		if (!raw) return
		const blob = JSON.parse(raw)
		if (blob?.survey && Object.keys(blob.survey).length > 0) {
			context.store.$patch(blob.survey)
		}
	} catch {
		/* ignore */
	}
}

export const useSurveyStore = defineStore('survey', {
	state: () => ({
		values: {
			height: null,
			weight: null,
			birthDate: null,
			targetWeight: null,
			secondsFromGMT: null,
			sex: '',
			intermittent_fasting: [],
			consents: [],
			dateEnd: null,
			agreed_to_use_ai_coach: false,

			data: {
				age: null,
				goal: ['lose_weight'],
				gender: [],
				strategy: [],
				lunch: [],
				feelings: [],
				hopings: [],
				happy_weight_1: [],
				happy_weight_2: [],
				meal_shedule: [],
				diet: [],
				meals: [],
				work: null,
				sport: ['not_at_all'],
				sleep: [],
				health_conditions: [],
				liveProblem: [],
				drugs: [],
				bodyType: null,
				carbs: [],
				water: null,
				measurement_system: null,
				measurement_system_height: null,
				measurement_system_weight: null,
				area: [],
				rq_1: [],
				rq_2: [],
				rq_3: [],
				rq_4: [],
				adt: [],
				motivation_event: [],
				carnivore_staples: [],
				motivation_event_custom: '',
				special_event: '',
				planUp: false,
				planDown: false,
				following_statement_1: null,
				following_statement_2: null,
				following_statement_3: null,
				following_statement_4: null,
				following_statement_5: null,
				following_statement_6: null,
				priorites: [],
				chronotype: [],
				cancel_reason: [],
				second_goal: [],

				at_happy_weight: [],
				confidence_in_reaching: [],
				current_priorities: [],
				eating_habits: [],
				gain_weight_reason: [],
				last_happy_weight: [],
				like_to_boost: [],
				feel: [],
				main_reason: [],
				mental_health_services: [],
				explore: [],
				motivation_to_lose_weight: [],
				see_my_self: [],
				snacking_triggers: [],
				max_weight: null,
				typical_day: [],
				typical_meal: [],
				understanding_mindset: [],
				weight_loss_apps: [],
				weight_loss_attempts: [],
				weight_loss_goal: [],
				weight_loss_goals: [],
				what_make_uncertain: [],
				commitment_answer: [],
				your_plan_ready: [],
				eating_disorder: [],
				pregnancy: [],
				gender_identity: null,
				non_binary_identity: null,
				dateEnd: null,
				utm_source: null,
			},
		},
		model: {},
		upsellOnboardingData: {},
		assessment: {},
		analyticData: {
			userStepCount: 1,
			onboardingStepCount: 1,
			requiredUserData: {},
		},
		weightPredicts: [],
		reports: {},
		wheelOfFortuneTurned: false,
	}),

	getters: {
		getWheelOfFortuneTurned(state) {
			return state.wheelOfFortuneTurned
		},
		getPropertyValue:
			(state) =>
			(property, isData = false, legacyAnalytics = false) => {
				let values = isData ? state.values.data[property] : state.values[property]
				if (legacyAnalytics && property in VALUES_MAP_FOR_LEGACY_ANALYTICS_BY_PROPERTY) {
					try {
						if (Array.isArray(values)) {
							values = values.map((v) => {
								return VALUES_MAP_FOR_LEGACY_ANALYTICS_BY_PROPERTY[property][v] ?? v
							})
						} else {
							values = VALUES_MAP_FOR_LEGACY_ANALYTICS_BY_PROPERTY[property][values] ?? values
						}
					} catch (e) {
						Sentry.captureException(e)
					}
				}
				return values
			},

		userWeightInKg: (state) => {
			return state.values.weight
		},

		userTargetWeightInKg: (state) => {
			return state.values.targetWeight
		},

		userWeightByUnitSystem(state) {
			const mainStore = useMainStore()
			return convertWeights(state.values.weight, mainStore.getWeightUnitSystem)
		},

		userTargetWeightByUnitSystem(state) {
			const mainStore = useMainStore()
			return convertWeights(state.values.targetWeight, mainStore.getWeightUnitSystem)
		},

		userHeightByUnitSystem(state) {
			const mainStore = useMainStore()
			return convertHeight(state.values.height, mainStore.getHeightUnitSystem)
		},

		userHeightInCm: (state) => state.values.height,

		userSex() {
			const sex = this.getPropertyValue('sex')

			if (!sex) {
				const error = new Error('User sex is not defined')
				Sentry.withScope((scope) => {
					scope.setExtras({
						sex: String(sex),
					})
					scope.setTags({
						requiredUserProperties: true,
					})
					scope.captureException(error)
				})

				return 'female'
			}

			return sex
		},

		userAge() {
			return this.getPropertyValue('age', true)
		},

		ageRange() {
			return ANALYTIC_AGE_TO_AGE_RANGE_MAP[this.userAge] ?? 'other'
		},

		getUserHasConsent(state) {
			return (consent) => {
				try {
					return state.values.consents.includes(consent)
				} catch {
					return false
				}
			}
		},

		getAssessment(state) {
			return state.assessment
		},

		getUserStepCount(state) {
			return state.analyticData.userStepCount
		},

		getOnboardingStepCount(state) {
			return state.analyticData.onboardingStepCount
		},

		brazeAttributes() {
			const mainStore = useMainStore()
			const locationStore = useLocationStore()
			let gender = 'u'

			if (this.userSex === 'female') {
				gender = 'f'
			} else if (this.userSex === 'male') {
				gender = 'm'
			}

			return {
				external_id: mainStore.getUserId,
				email: mainStore.getUserEmail,
				age: this.userAge,
				gender: gender,
				region_name: mainStore.getGeo.regionName ? capitalize(mainStore.getGeo.regionName) : null,
				country: mainStore.getCountryCodeUppercase,
				language: window.language.substr(0, 2),
				custom_attrs: {
					funnel: locationStore.getAppAreaConfig.funnel.slug,
					initial_funnel: locationStore.getInitialFunnelType,
					currency: mainStore.getCurrency,
					height: this.userHeightInCm ? round(this.userHeightInCm, 2) : null,
					weight: this.userWeightInKg ? round(this.userWeightInKg, 2) : null,
					original_weight: this.userWeightInKg ? round(this.userWeightInKg, 2) : null,
					target_weight: this.userTargetWeightInKg ? round(this.userTargetWeightInKg, 2) : null,
				},
			}
		},

		getReportUrl(state) {
			return state.reportUrl
		},

		getWeightPredicts(state) {
			return state.weightPredicts
		},

		/**
		 * 4 predicts available in total. Counts from 1 to 4
		 * implementation nuances is in WeightPredictor
		 */
		getWeightPredictDateByStep() {
			return (step) => this.getWeightPredicts?.[step - 1]?.targetDate
		},

		getReportUrlById: (state) => (id) => state.reports[id],

		onboardingData(state) {
			const onboardingData = {}

			// Every value in onboardingData should be an array of strings or null
			Object.entries(state.values.data).forEach(([key, value]) => {
				if (Array.isArray(value) && value.length) {
					onboardingData[key] = value
				} else if (value) {
					onboardingData[key] = [String(value)]
				} else {
					onboardingData[key] = null
				}
			})

			return onboardingData
		},
	},

	actions: {
		updateSurveyModel(data) {
			this.model = data
		},

		setUpsellOnboardingData(data) {
			this.upsellOnboardingData = data
		},

		setReportUrl(data) {
			this.reports = {
				...this.reports,
				...data,
			}
		},

		updateAssessment(data) {
			this.assessment = data
		},

		/**
		 * @param data {Object}
		 * @param data.isData {Boolean}
		 * @param data.property {String}
		 * @param data.value {any}
		 */
		updateProperty(data) {
			if (data.isData) {
				this.values.data[data.property] = data.value
			} else {
				this.values[data.property] = data.value
			}
			this.changed = true
		},

		addUserConsent(consent) {
			const consents = this.values.consents
			if (!consents.includes(consent)) {
				consents.push(consent)
			}
		},
		removeUserConsent(consent) {
			const consents = this.values.consents
			const index = consents.indexOf(consent)
			if (index !== -1) {
				consents.splice(index, 1)
			}
		},

		incrementUserStep() {
			this.analyticData.userStepCount += 1
		},

		setOnboardingStepCount(count) {
			this.analyticData.onboardingStepCount = count
		},

		resetAnalyticDataCounter() {
			this.analyticData.userStepCount = 1
			this.analyticData.onboardingStepCount = 1
		},

		setRequiredUserData(data) {
			this.analyticData.requiredUserData = {
				...this.analyticData.requiredUserData,
				...data,
			}
		},
		setWeightPredicts(data) {
			this.weightPredicts = data
		},

		setWheelOfFortuneTurned(data) {
			this.wheelOfFortuneTurned = data
		},

		async saveSurveyData(params = {}) {
			const locationStore = useLocationStore()

			this.values.data.utm_source = locationStore.getRawQueryParam('utm_source')

			const metaData = {}

			Object.entries(this.values).forEach(([key, value]) => {
				if (key !== 'data') {
					metaData[key] = value
				}
			})

			let time_offset = -new Date().getTimezoneOffset() * 60
			let response = await api.updateUser({
				...this.values,
				locale: useI18nStore().getLanguage,
				secondsFromGMT: time_offset,
				platformFirst: 'web',
				funnel: locationStore.getAppAreaConfig.appArea,
				...params,
				onboardingData: this.onboardingData,
				metaData,
			})

			return response.data
		},

		async saveFunnelTags() {
			const mainStore = useMainStore()
			const locationStore = useLocationStore()
			const subscriptionStore = useSubscriptionStore()
			const { options } = locationStore.getAppAreaConfig
			const initialUserTags = locationStore.getInitialUserTags

			const tagsToSave = Array.isArray(initialUserTags) ? initialUserTags : options?.userTags

			if (subscriptionStore.hasSubscription) {
				return
			}

			try {
				if (Array.isArray(tagsToSave)) {
					await api.saveFunnelTags({
						tags: tagsToSave,
					})
				}
			} finally {
				// merge user even if tags saving failed, cause we need to merge onboardingData
				if (mainStore.hasAlreadyExistedAccount) {
					api.mergeUserWithAlreadyExisted()
				}
			}
		},

		async updateSurveyData(data) {
			let time_offset = -new Date().getTimezoneOffset() * 60

			let response = await api.updateUser({
				...this.values,
				name: data.name,
				email: data.email,
				secondsFromGMT: time_offset,
			})

			return response.data
		},

		handleUserAnalyticData(data) {
			const locationStore = useLocationStore()
			const isDataHasValues = data && Object.keys(data).length
			if (!isDataHasValues) {
				return
			}

			const validateRequiredDataProperty = (key) => {
				try {
					const isPropertyValueVaild = REQUIRED_ANALYTIC_PROPERTIES_VALUES_MAP[key].includes(data[key])

					if (!isPropertyValueVaild) {
						throw new Error('Analytic Events Validation: Invalid value', {
							cause: {
								appArea: locationStore.getAppAreaConfig.appArea,
								funnelId: locationStore.getAppAreaConfig.funnel.id,
								wrongData: {
									[key]: data[key],
								},
							},
						})
					}
				} catch (error) {
					Sentry.withScope((scope) => {
						// Stable message → one Sentry issue; the offending property goes to a
						// searchable/filterable tag (e.g. `invalidUserProperty:Sex`).
						scope.setTags({
							requiredUserProperties: true,
							invalidUserProperty: key,
						})
						scope.captureException(error)
					})
				}
			}

			for (const key in data) {
				if (REQUIRED_ANALYTIC_PROPERTIES.includes(key)) {
					if (REQUIRED_ANALYTIC_PROPERTIES_VALUES_MAP[key]) {
						validateRequiredDataProperty(key)
					}

					this.setRequiredUserData({ [key]: data[key] })
				}
			}
		},

		validateUserAnalyticData() {
			const mainStore = useMainStore()
			const locationStore = useLocationStore()
			const requiredData = this.analyticData.requiredUserData
			const missingData = []

			try {
				for (const key of REQUIRED_ANALYTIC_PROPERTIES) {
					if (!requiredData[key]) {
						missingData.push(key)
					}
				}

				if (missingData.length) {
					throw new Error('Analytic Events Validation: Required user properties missed', {
						cause: {
							email: mainStore.getUserEmail,
							appArea: locationStore.getAppAreaConfig.appArea,
							funnelId: locationStore.getAppAreaConfig.funnel.id,
							missingData,
						},
					})
				}
			} catch (error) {
				Sentry.withScope((scope) => {
					// Stable message → all these group into one Sentry issue. The missing
					// property list lives in a searchable tag instead of the message, so
					// grouping stays clean while you can still filter by what's missing
					// (e.g. `missingUserProperties:Height` for single-prop cases) and read
					// the tag-value breakdown to see which property fails most often.
					scope.setTags({ requiredUserProperties: true })
					if (missingData.length) {
						scope.setTag('missingUserProperties', missingData.join(','))
					}
					scope.captureException(error)
				})
			}
		},
	},

	persist: {
		...sharedKeyPersist('vuex', 'survey', weeklyStorage),
		afterRestore: _patchFromSessionStorage,
	},
})
